import changeLocalClient from '@/utilites/change-local-client';
import isStringArrayDefined from '@/utilites/is-string-array-defined';
import isStringDefined from '@/utilites/is-string-defined';
import translationFallback from '@/utilites/translations/translationFallback';
import userSettingsDataValidation from '@/utilites/user-settings-data-validation';
import validateUserAddressNameInput from '@/utilites/validate-user-address-name-input';
import validateUserBusinessNameInput from '@/utilites/validate-user-business-name-input';
import { BusinessInfo, UserDTO, ToastMessagesKeys } from '../types/other';
import { countriesWithFlagsAndPrefix } from '@/constants/countries-with-flag';
import { dismissToast, getToastData, showToast } from '@/utilites/toast-utils';
import { format, isValid, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { UserQueryOptions } from '@/types/useQuery';
import { useTranslation } from 'next-i18next';
import usePhonePrefix from './usePhonePrefix';
import event from '@/utilites/gTag';
import { validateUserData } from '@/utilites/user/user.validation.utils';
import useBackendUser from '@/hooks/useBackendUser';

export default function useModifyUser(
  options?: UserQueryOptions,
  actionAfterSuccess?: (() => void) | (() => Promise<void>),
  actionAfterError?: (() => void) | (() => Promise<void>),
  initialData?: UserDTO | null
) {
  const data = useBackendUser();
  const { t: tDashboard } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const [user, setUser] = useState<UserDTO>({} as UserDTO);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isManuallyRefetching, setIsManuallyRefetching] = useState(false);
  const [isBusinessOwner, setIsBOwner] = useState(false);
  const { currentPhonePrefix, setCurrentPhonePrefix } = usePhonePrefix();
  const [canSubmitForm, setCanSubmitForm] = useState(false);

  const queryClient = useQueryClient();

  const router = useRouter();

  const [isApiCountry, setIsApiCountry] = useState(false);
  const [loadingSettingInfo, setLoadingSettingInfo] = useState(true);

  useEffect(() => {
    if (data.data) {
      setUser(data.data);

      // businessData
      const { businessTaxId = null, businessName = null } = data.data?.Business || {};
      const isBowner = isStringArrayDefined([businessTaxId, businessName]);
      setIsBOwner(isBowner);
      // user data
      const { countryCode = null } = data.data?.userAddress || {};
      const isCountryDefined = isStringDefined(countryCode);
      if (isCountryDefined) {
        setIsApiCountry(true);
      }
      // phone number
      if (data.data.phonePrefix) {
        setCurrentPhonePrefix(data.data.phonePrefix);
      }
      setLoadingSettingInfo(false);
    }
  }, [data.data, setCurrentPhonePrefix]);

  const setIsBusinessOwner = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsBOwner(e.target.checked);
  };

  const refetchUserData = async () => {
    try {
      setIsManuallyRefetching(true);
      await queryClient.invalidateQueries({
        queryKey: ['user_v2'],
      });
      await queryClient.refetchQueries({
        queryKey: ['user_v2'],
      });
      setIsManuallyRefetching(false);
    } catch (err) {
      console.error('Error refetching user data:', err);
    }
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = parseISO(e.target.value);
    const isValidDate = isValid(inputDate);
    if (!isValidDate) return;
    setUser(prev => {
      return {
        ...prev,
        dob: format(inputDate, 'yyyy-MM-dd'),
      };
    });
  };

  const handleUserAddressChange = (e: React.SyntheticEvent) => {
    const { name, value } = e.target as HTMLInputElement;
    const isNameValid = validateUserAddressNameInput(name);
    if (isNameValid) {
      setUser(prev => ({
        ...prev,
        userAddress: {
          ...prev.userAddress,
          [name]: value,
        },
      }));
    }
  };

  const handleUserBusinessChange = (e: React.SyntheticEvent) => {
    const { name, value } = e.target as HTMLInputElement;
    const isNameValid = validateUserBusinessNameInput(name);
    if (isNameValid && user) {
      if (Boolean(user?.Business) === false) {
        const newUser = { ...user, Business: {} as BusinessInfo };
        const nameKey = name as 'businessName' | 'businessTaxId' | 'businessNumber';
        newUser['Business'] = {
          ...newUser.Business,
          [`${nameKey}`]: value,
        };
        setUser(newUser);
      } else {
        const newUser = {
          ...user,
          Business: { ...user.Business } as BusinessInfo,
        };
        const nameKey = name as 'businessName' | 'businessTaxId' | 'businessNumber';
        newUser['Business'] = {
          ...newUser.Business,
          [`${nameKey}`]: value,
        };
        setUser(newUser);
      }
    }
  };
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    return setUser(prev => {
      return {
        ...prev,
        phoneNumber: value.trim(),
        displayPhoneNumber: value.trim(),
      };
    });
  };

  const handleUserChange = async (e: React.SyntheticEvent) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === 'phoneNumber') {
      return handlePhoneNumberChange(e as React.ChangeEvent<HTMLInputElement>);
    }

    if (name === 'local') {
      setIsManuallyRefetching(true);
      await changeLocalClient(value, router);
      setIsManuallyRefetching(false);
    }
    setUser((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const doUserSubmission = async (
    userParam: UserDTO
  ): Promise<{
    error: boolean;
    message: string;
  }> => {
    // Early return if already loading
    if (isLoadingSubmit) {
      return { error: true, message: 'loading' };
    }

    // Validate input
    if (!userParam) {
      console.error('User data is undefined');
      showToast('anUnexpectedErrorOccurred', 'error', tCommon);
      return { error: true, message: 'anUnexpectedErrorOccurred' };
    }

    // Prepare validated user data
    const { error: errorUser, userValidated } = userSettingsDataValidation({
      ...userParam,
      phoneNumber: `${currentPhonePrefix.prefix}${user?.phoneNumber}`,
    });

    // Handle validation errors
    if (errorUser) {
      showToast(errorUser, 'error', tDashboard);
      return { error: true, message: errorUser };
    }

    // Start loading state
    setIsLoadingSubmit(true);

    // Log event
    event(
      'save_profile',
      {
        event_category: 'interaction',
        device: 'Website',
      },
      { userId: userParam.id.toString(), lang: userParam.local }
    );

    // Show loading toast
    const toastId = showToast('savingYourProfile', 'loading', tDashboard);

    try {
      // Make API call
      const response = await fetch('/api/create-user-profile-v2', {
        method: 'POST',
        body: JSON.stringify({ ...userValidated }),
        headers: { 'Content-Type': 'application/json' },
      });

      // Parse response
      const data: Record<'error' | 'message', ToastMessagesKeys> = await response.json();

      // Dismiss loading toast
      await dismissToast(toastId);

      // Handle successful response
      if (response.ok || data.message === 'successSavingYourProfile') {
        // Change local settings
        await changeLocalClient(userValidated.local, router);

        // Show success toast
        showToast(data.message || 'successSavingYourProfile', 'success', tDashboard);

        // Refetch user data and run post-success action
        await refetchUserData();
        if (actionAfterSuccess) {
          await actionAfterSuccess();
        }

        // Reset loading state
        setIsLoadingSubmit(false);

        return { error: false, message: data.message };
      }

      // Handle Stripe-specific errors
      if ('stripeError' in data && typeof data.stripeError === 'string') {
        const stripeError = data.stripeError.toLowerCase();

        // Handle invalid postal code
        if (stripeError.includes('invalid') && stripeError.includes('postal code')) {
          setIsLoadingSubmit(false);

          const { userAddress } = userValidated;
          if (userAddress) {
            const { countryCode } = userAddress;
            const country = countriesWithFlagsAndPrefix.find(
              c => c.cc.toLowerCase() === String(countryCode).toLowerCase()
            );

            if (country) {
              const countryTranslated = translationFallback(
                country.name.toLowerCase(),
                tCommon(country.name.toLowerCase()),
                country.name
              );

              toast.error(
                translationFallback(
                  'settings.your_postal_code_is_invalid',
                  tDashboard('settings.your_postal_code_is_invalid'),
                  'Your postal code for {{country}} is invalid'
                ).replace('{{country}}', countryTranslated)
              );

              return { error: true, message: 'invalidPostalCode' };
            }
          }
        }

        // Handle invalid phone number
        if (stripeError.includes('not a valid phone number')) {
          setIsLoadingSubmit(false);
          toast.error(
            translationFallback(
              'settings.your_phone_number_is_invalid',
              tDashboard('settings.your_phone_number_is_invalid'),
              'Your phone number is invalid'
            )
          );
          return { error: true, message: 'yourPhoneNumberIsInvalid' };
        }
      }

      // Throw for other unexpected errors
      throw new Error('anUnexpectedErrorOccurred');
    } catch (error: unknown) {
      console.error('Error submitting user data:', error);
      // Reset loading state
      setIsLoadingSubmit(false);

      // Handle specific error scenarios
      if (error instanceof Error) {
        if (error.message === 'anUnexpectedErrorOccurred') {
          showToast(error.message, 'error', tCommon);
          return { error: true, message: error.message };
        }
      }

      // Dismiss toast and show generic error
      await dismissToast(
        toastId,
        undefined,
        getToastData('anUnexpectedErrorOccurred', 'error', tCommon)
      );
      showToast('anUnexpectedErrorOccurred', 'error', tCommon);

      // Run error callback if provided
      if (actionAfterError) {
        await actionAfterError();
      }

      return { error: true, message: 'anUnexpectedErrorOccurred' };
    }
  };

  const submitUser = async (
    e: React.SyntheticEvent
  ): Promise<{ error: boolean; message: string }> => {
    // Prevent default form submission behavior
    e.preventDefault();

    try {
      // Perform user submission and get result
      const { error, message } = await doUserSubmission(user);

      // Only refetch if submission was successful
      if (!error) {
        await refetchUserData();
      }

      return { error, message };
    } catch (err) {
      // Fallback error handling
      console.error('User submission failed:', err);
      return {
        error: true,
        message: 'anUnexpectedErrorOccurred',
      };
    }
  };
  useEffect(() => {
    validateUserData(user, currentPhonePrefix.prefix).then(res => {
      setCanSubmitForm(res.isValid);
    });
  }, [user, currentPhonePrefix]);

  return {
    data: data,
    user,
    setUser,
    isLoadingSubmit,
    isManuallyRefetching,
    handleUserChange,
    submitUser,
    handleDobChange,
    handleUserAddressChange,
    handleUserBusinessChange,
    refetchUserData,
    isBusinessOwner,
    setIsBOwner,
    currentPhonePrefix,
    setCurrentPhonePrefix,
    handlePhoneNumberChange,
    setIsBusinessOwner,
    loadingSettingInfo,
    isApiCountry,
    canSubmitForm,
    setCanSubmitForm,
  };
}
